import React, {useEffect, useRef, useState} from 'react';
import {ActivityIndicator, Animated, Easing, Pressable, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';

import {canRecord, Recorder, startRecording} from '../coachVoice';
import {F} from '../courseTheme';

type Props = {
  /** Transcribe a recording into the question box. */
  onTranscribe: (audio: Blob) => Promise<string | null>;
  onText: (text: string) => void;
  disabled?: boolean;
  /** Lets the composer give the whole row over to the recording bar. */
  onRecordingChange?: (recording: boolean) => void;
};

const BARS = 18;

/**
 * Ask out loud.
 *
 * Three states, visibly different: idle, recording (orange, live waveform,
 * running clock, cancel and send), and transcribing (teal spinner). The
 * waveform is driven by the real input level rather than by a timer, so a
 * muted microphone looks dead instead of looking like it is listening — which
 * is the difference between "it is not hearing me" and "it is thinking".
 *
 * Orange rather than red: red already means "wrong move" and "could not
 * answer" everywhere else in this player, and a red mic reads as an error.
 */
export default function CourseMicButton({
  onTranscribe,
  onText,
  disabled,
  onRecordingChange,
}: Props) {
  const [state, setState] = useState<'idle' | 'recording' | 'working'>('idle');
  const [seconds, setSeconds] = useState(0);
  const [levels, setLevels] = useState<number[]>(() => new Array(BARS).fill(0));
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<Recorder | null>(null);

  useEffect(() => {
    onRecordingChange?.(state === 'recording');
  }, [state, onRecordingChange]);

  // Sampling the live level and ticking the clock. Both stop with the state,
  // so nothing is left running once the recording ends.
  useEffect(() => {
    if (state !== 'recording') return;
    const meter = setInterval(() => {
      const level = recorderRef.current?.level() ?? 0;
      setLevels(prev => [...prev.slice(1), level]);
    }, 80);
    const clock = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => {
      clearInterval(meter);
      clearInterval(clock);
    };
  }, [state]);

  const begin = async () => {
    if (disabled || state !== 'idle') return;
    setError(null);
    try {
      recorderRef.current = await startRecording();
      setSeconds(0);
      setLevels(new Array(BARS).fill(0));
      setState('recording');
    } catch {
      // Permission denied, or no microphone. Typing still works, so this is a
      // note rather than a failure.
      setError('No microphone access — you can still type.');
    }
  };

  const finish = async () => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    setState('working');
    const audio = await recorder.stop();
    recorderRef.current = null;
    if (!audio) {
      setState('idle');
      return;
    }
    try {
      const text = await onTranscribe(audio);
      if (text) onText(text);
      else setError('Could not make that out. Try again, or type it.');
    } catch {
      setError('Could not make that out. Try again, or type it.');
    }
    setState('idle');
  };

  const abandon = () => {
    recorderRef.current?.cancel();
    recorderRef.current = null;
    setState('idle');
  };

  if (!canRecord()) return null;

  if (state === 'recording') {
    return (
      <View style={{flex: 1, gap: 4}}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
            backgroundColor: F.hintBg,
            borderWidth: 1,
            borderColor: F.hintFg,
            borderRadius: F.radius.button,
            paddingHorizontal: 12,
            paddingVertical: 8,
          }}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Discard this recording"
            onPress={abandon}>
            <Ionicons name="close" size={18} color={F.mutedText} />
          </Pressable>

          <View
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              height: 22,
            }}>
            {levels.map((level, i) => (
              <View
                key={i}
                style={{
                  width: 3,
                  borderRadius: 2,
                  backgroundColor: F.hintFg,
                  // A floor of 3px so the bar is visible while silent; a
                  // flat line is information, not a rendering bug.
                  height: Math.max(3, Math.round(level * 22)),
                  opacity: 0.55 + level * 0.45,
                }}
              />
            ))}
          </View>

          <CCText
            style={{fontSize: 13, fontWeight: '600', color: F.brownDark}}>
            {`${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`}
          </CCText>

          {/* "Send", not "Stop": stopping is what the ✕ does. This is the
              button that asks the question. */}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Send this recording"
            onPress={finish}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 5,
              borderRadius: 999,
              backgroundColor: F.hintFg,
              paddingHorizontal: 12,
              paddingVertical: 6,
            }}>
            <Ionicons name="send" size={13} color={F.white} />
            <CCText style={{fontSize: 13, fontWeight: '700', color: F.white}}>
              Send
            </CCText>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View style={{gap: 4}}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Ask your coach out loud"
        onPress={state === 'idle' ? begin : undefined}
        disabled={disabled || state === 'working'}
        style={{
          width: 42,
          height: 42,
          borderRadius: 999,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: state === 'working' ? F.teal : F.badgeBg,
          borderWidth: 1,
          borderColor: 'rgba(38,35,34,0.18)',
          opacity: disabled ? 0.5 : 1,
        }}>
        {state === 'working' ? (
          <ActivityIndicator color={F.white} size="small" />
        ) : (
          <Ionicons name="mic-outline" size={20} color={F.brown} />
        )}
      </Pressable>
      {error ? (
        <CCText style={{fontSize: 11, fontWeight: '500', color: F.wrongFg}}>
          {error}
        </CCText>
      ) : null}
    </View>
  );
}
